import { useEffect, useState } from "react";
import { useNavigate, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { supabase } from "./supabase";
import Header from "./components/Header";
import AdminHeader from "./components/AdminHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import SlotsSignIn from "./components/SlotSignIn";
import SlotsSignUp from "./components/SlotSignUp";
import MainContent from "./components/MainContent";
import SetPassword from "./components/SetPassword";
import { Toaster } from "react-hot-toast";
import UsersTablePage from "./components/UsersTablePage";
import { useRef } from "react";
import { LicenseInfo } from "@mui/x-license";
import OutreachFlow from "./components/OutreachFlow";
import Archive from "./components/Archive";
import Settings from "./components/Settings";
import HeroBannerDemo from "./components/HeroBannerDemo";
import { MainContextProvider } from "./context/useMainContext";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./assets/theme";
import ConsentManager from "./components/ConsentManager";
LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_KEY);

export default function App() {
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirectedRef = useRef(false);
  const hideHeader = location.pathname === "/";

  useEffect(() => {
    const checkSession = async () => {
      setLoading(true);
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session) {
        setIsLoggedIn(true);
        await fetchUser(session.user.id);
      } else {
        setIsLoggedIn(false);
        setUser(null);
      }

      setLoading(false);
    };

    checkSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((_, session) => {
      if (session) {
        setIsLoggedIn(true);
        fetchUser(session.user.id);
      } else {
        setIsLoggedIn(false);
        setUser(null);
      }
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  const fetchUser = async (userId) => {
    const { data, error } = await supabase.from("user").select("*").eq("id", userId).single();

    if (error) {
      console.error("Error fetching user:", error);
    } else {
      setUser(data);
    }
  };

  useEffect(() => {
    if (!loading && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;

      // Don't redirect if you're on allowed public or admin-only routes
      const allowList = ["/signup", "/reset-password", "/create-user"];
      if (!allowList.includes(location.pathname)) {
        if (isLoggedIn) {
          navigate("/dashboard" + location.search);
        } else {
          navigate("/");
        }
      }
    }
  }, [isLoggedIn, loading, navigate, location.pathname, location.search]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <Toaster position="top-center" />
        {!hideHeader && (user?.role === "admin" ? <AdminHeader user={user} /> : <Header user={user} />)}

        <Box sx={{ 
          marginTop: "64px", 
          flexGrow: 1, 
          alignItems: "center",
          height: "calc(100vh - 64px)",
        }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
                width: "100%",
              }}
            >
              <CircularProgress size={40} sx={{ color: "#667eea" }} />
            </Box>
          ) : (
            <Routes>
              <Route path="/" element={<SlotsSignIn />} />
              <Route path="/signup" element={<SlotsSignUp />} />
              <Route
                path="/dashboard"
                element={
                  <MainContextProvider user={user}>
                    <MainContent />
                  </MainContextProvider>
                }
              />
              <Route path="/reset-password" element={<SetPassword />} />
              <Route path="/create-user" element={<UsersTablePage user={user} />} />
              <Route path="/outreach" element={<OutreachFlow />} />
              <Route path="/archive" element={<Archive />} />
              <Route
                path="/settings"
                element={user?.role === "admin" ? <Settings /> : <Navigate to="/dashboard" replace />}
              />
              <Route path="/demo" element={isLoggedIn ? <HeroBannerDemo /> : <Navigate to="/" replace />} />
            </Routes>
          )}
        </Box>
      </Box>
      
      <ConsentManager />
    </ThemeProvider>
  );
}
